import { useCallback, useEffect, useState } from "react";
import { QuizQuestionProps } from "@/pages/quiz/context/types";
import { calculateBMI, sanitizeDecimal } from "@/utils";
import {
  BMI_ERROR_MESSAGE,
  ONLY_NUMBERS,
} from "@/constants";
import { Button } from "@/pages/quiz/15000/components/Button";
import { Input } from "@/pages/quiz/15000/components/Input";
import { Select } from "@/pages/quiz/15000/components/Select";
import {
  DEFAULT_RANGE,
  getWeightTooHighError,
  getWeightTooLowError
} from "@/utils/validateField/weight";

export type HeightWeightAnswer = {
  feet: string;
  inches: string;
  weight: string;
};

enum InputKey {
  FEET = "feet",
  WEIGHT = "weight",
  INCHES = "inches",
}

const feetOptions = Array.from({ length: 4 }, (_, i) => ({
  value: (i + 4).toString(),
  label: (i + 4).toString(),
}));

const inchesOptions = Array.from({ length: 12 }, (_, i) => ({
  value: i.toString(),
  label: i.toString(),
}));

const NUMBER_OF_ANSWERS = 3;

const HeightWeightInputsWithBMI = ({
  questionValue,
  setQuestionValue,
  setQuestionAnswer,
}: QuizQuestionProps<HeightWeightAnswer>) => {
  const [errorMessage, setErrorMessage] = useState("");

  const isAllInputsFilled =
    Object.values(questionValue).filter(Boolean).length === NUMBER_OF_ANSWERS;

  useEffect(() => {
    // reset error on any input
    setErrorMessage("");
  }, [questionValue]);

  const { weight, feet, inches } = questionValue;

  const currentBMI =
    weight && feet && inches
      ? calculateBMI(+weight, +feet, +inches).toFixed(2)
      : 0;

  const onSubmitAnswer = () => {
    const { weight, feet, inches } = questionValue;
    const bmi = calculateBMI(+weight, +feet, +inches);

    if (Number(weight) < DEFAULT_RANGE.min) {
      setErrorMessage(getWeightTooLowError(DEFAULT_RANGE.min));
      return;
    }

    if (Number(weight) > DEFAULT_RANGE.max) {
      setErrorMessage(getWeightTooHighError(DEFAULT_RANGE.max));
      return;
    }

    if (bmi < 27) {
      setErrorMessage(BMI_ERROR_MESSAGE);
      return;
    }

    setErrorMessage("");

    setQuestionAnswer(questionValue);
  };

  const handleInputChange = useCallback(
    (inputValue: string, inputKey: string) => {
      let cleanedInput = inputValue.replace(ONLY_NUMBERS, "");
      setQuestionValue({ ...questionValue, [inputKey]: cleanedInput });
    },
    [questionValue]
  );

  const handleWeightChange = useCallback(
    (inputValue: string) => {
      let cleanedInput = sanitizeDecimal(inputValue);
      setQuestionValue({ ...questionValue, weight: cleanedInput });
    },
    [questionValue]
  );

  return (
    <>
      <div className="w-full flex flex-col gap-6">
        <div className="flex w-full flex-col gap-4">
          <div className="quiz-description text-center text-sm font-medium">
            Your BMI
            <p className="text-4xl font-bold ">{currentBMI || "0.0"}</p>
          </div>
          <input
            value={currentBMI}
            onChange={() => {}}
            type="range"
            min="0"
            max="40"
            step="1"
            className="pointer-events-none h-4 w-full appearance-none rounded-full bg-gradient-to-r from-dark-purple-2 to-soft accent-white outline-none"
          />
        </div>

        <div className="w-full flex justify-between items-center gap-2.5 sm:gap-3 lg:gap-4">
          <Select
            label="Feet"
            placeholder="0"
            options={feetOptions}
            value={feetOptions.find(
              (option) => option.value === questionValue?.["feet"]
            )}
            onValueChange={(v) => handleInputChange(v, InputKey.FEET)}
          />
          <Select
            label="Inches"
            placeholder="0"
            options={inchesOptions}
            value={inchesOptions.find(
              (option) => option.value === questionValue["inches"]
            )}
            onValueChange={(v) => handleInputChange(v, InputKey.INCHES)}
          />
        </div>

        <Input
          label="Current weight (lbs.)"
          placeholder="0"
          value={questionValue?.["weight"] || ""}
          onChange={(e) => handleWeightChange(e.target.value)}
          errorMessage={errorMessage}
        />
      </div>

      <Button
        disabled={!isAllInputsFilled || !!errorMessage}
        onClick={onSubmitAnswer}
      />
    </>
  );
};

export { HeightWeightInputsWithBMI };
